import { type ApiClientError } from 'shared/api/fetch';
import { AUTH_SESSION_MISSING, SESSION_EXPIRED_REASON } from 'shared/api/authSession';
import { makeErrorToast } from './Toaster';
import { useEffect } from 'react';
import { logAndReportError } from 'shared/errorReporter';
import { HttpStatusCodes } from 'shared/httpStatus';
import { sendGTMEvent } from 'shared/googleTagManager';
import { removeStoredAuthToken } from 'src/auth/authToken';
import { APP_ROUTES } from 'shared/routes';
import { getReturnTo, redirectToPage } from 'shared/utils';
const ACCESS_TOKEN_ERROR_MESSAGES = ['Invalid access token', 'Access token expired'];
declare global {
  interface WindowEventMap {
    'client-error': CustomEvent<ApiClientError>;
  }
}
export const dispatchClientError = (event: ApiClientError): void => {
  window.dispatchEvent(new CustomEvent('client-error', {
    detail: event
  }));
};
let isHandlingInvalidAccessTokenError = false;
const isSessionExpiry = (error: ApiClientError): boolean => error.status_code === HttpStatusCodes.UNAUTHORIZED && (error.code === AUTH_SESSION_MISSING || ACCESS_TOKEN_ERROR_MESSAGES.includes(error.message));

/**
 * Clears the stored auth token and sends the user to sign-in once.
 * Runs when the access token is rejected and when a BFF route reports a missing session,
 * including after the auth cookie is already gone. Already-open sign-in stays put.
 */
export const handleInvalidAccessTokenError = (): void => {
  if (isHandlingInvalidAccessTokenError || window.location.pathname === APP_ROUTES.auth.signInPage) {
    return;
  }
  isHandlingInvalidAccessTokenError = true;
  removeStoredAuthToken();
  const returnTo = encodeURIComponent(getReturnTo());
  redirectToPage(`${APP_ROUTES.auth.signInPage}?returnTo=${returnTo}&reason=${SESSION_EXPIRED_REASON}`);
};
const ApiClientErrorHandler = () => {
  useEffect(_temp2);
  return null;
};
export default ApiClientErrorHandler;
function _temp(event) {
  const error = event.detail;
  if (isSessionExpiry(error)) {
    handleInvalidAccessTokenError();
    return;
  }
  logAndReportError(error);
  sendGTMEvent({
    event: "api_error",
    endpoint: error.endpoint ?? "unknown",
    status_code: error.status_code,
    error_message: error.message,
    page_path: globalThis.location.pathname,
    timestamp: new Date().toISOString()
  });
  if (error.status_code >= HttpStatusCodes.INTERNAL_SERVER_ERROR) {
    makeErrorToast("Oops! Something unexpected occurred on our side. Please try again later or contact support if the issue persists.");
  }
}
function _temp2() {
  const handleClientError = _temp;
  window.addEventListener("client-error", handleClientError);
  return () => {
    window.removeEventListener("client-error", handleClientError);
  };
}